/**
 * Municipalities ranked by district-heating potential.
 *
 * The one view in the product that crosses municipality boundaries, which is
 * why it is a dialog rather than a panel: it is a way in to the map, not a
 * reading of what is currently on it. Picking a row loads that municipality and
 * switches the street network on, because a ranking of network potential that
 * drops you on a map with no network showing answers a question you did not ask.
 *
 * Fetched once per session and kept — the ranking is computed over the whole
 * territory and does not change between two opens of the same page.
 */

import { useEffect, useRef, useState } from "react";

import { ApiError, api } from "../api/client";
import type { NetworkRanking } from "../api/client";
import { fmt, fmtPct } from "./lib/format";
import { VIAB_COLORS } from "./lib/tramos";

interface Props {
  open: boolean;
  onClose: () => void;
  onPick: (municipio: string) => void;
}

/** How many rows the endpoint is asked for. The list scrolls; 80 is the tail. */
const TOP = 80;

type Row = NetworkRanking["top"][number];

/** k€ below a million, M€ above: six-digit euros in a table cell do not read. */
function money(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "–";
  return value >= 1e6 ? `${fmt(value / 1e6, 1)} M€` : `${fmt(value / 1000)} k€`;
}

export function RankingModal({ open, onClose, onPick }: Props) {
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open || rows || error) return;
    api.networkRanking(TOP)
      .then((data) => setRows(data.top))
      .catch((err) => setError(err instanceof ApiError
        ? err.message
        : "No se ha podido cargar el ranking."));
  }, [open, rows, error]);

  // Focus moves into the dialog when it opens, so the next Tab lands inside it
  // rather than continuing through the map behind.
  useEffect(() => { if (open) closeRef.current?.focus(); }, [open]);

  if (!open) return null;

  return (
    <div
      className="modal-backdrop"
      // Only a click that started and ended on the backdrop closes: dragging a
      // selection out of the table would otherwise dismiss the dialog.
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="ranking-title">
        <header className="modal-head">
          <h2 id="ranking-title">Ranking de municipios · potencial de redes</h2>
          <button type="button" ref={closeRef} className="modal-close"
                  aria-label="Cerrar" onClick={onClose}>✕</button>
        </header>

        <p className="modal-key">
          <span><b style={{ color: VIAB_COLORS.ambos }}>Ambos</b> 4G + 5G viables</span>
          <span><b style={{ color: VIAB_COLORS["4g"] }}>4G</b> densidad ≥ 1,5 MWh/m·año</span>
          <span><b style={{ color: VIAB_COLORS["5g"] }}>5G</b> LCOE geo &lt; aero</span>
          <span><b style={{ color: VIAB_COLORS.aero }}>Aero</b> individual gana</span>
          <span><b>Score</b> = 1,0·%Ambos + 0,7·%4G + 0,5·%5G</span>
        </p>

        <div className="modal-body">
          {error && <p className="modal-error">{error}</p>}
          {!error && !rows && <p className="modal-loading">Cargando ranking…</p>}
          {rows && (
            <table className="rank-table">
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Municipio</th>
                  <th scope="col" className="n">km totales</th>
                  <th scope="col" className="n" style={{ color: VIAB_COLORS.ambos }}>% Ambos</th>
                  <th scope="col" className="n" style={{ color: VIAB_COLORS["4g"] }}>% 4G</th>
                  <th scope="col" className="n" style={{ color: VIAB_COLORS["5g"] }}>% 5G</th>
                  <th scope="col" className="n" style={{ color: VIAB_COLORS.aero }}>% Aero</th>
                  <th scope="col" className="n">Score</th>
                  <th scope="col" className="n">Ahorro red/año</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.nom_mun}>
                    <td className="rank-n">{i + 1}</td>
                    <th scope="row">
                      <button type="button" onClick={() => onPick(r.nom_mun)}>
                        {r.nom_mun}
                      </button>
                    </th>
                    <td className="n muted">{fmt(r.km_total, 1)}</td>
                    <td className="n" style={{ color: VIAB_COLORS.ambos }}>{fmtPct(r.pct_ambos)}</td>
                    <td className="n" style={{ color: VIAB_COLORS["4g"] }}>{fmtPct(r.pct_4g)}</td>
                    <td className="n" style={{ color: VIAB_COLORS["5g"] }}>{fmtPct(r.pct_5g)}</td>
                    <td className="n muted">{fmtPct(r.pct_aero)}</td>
                    <td className="n score">{fmt(r.score_redes, 1)}</td>
                    <td className="n saving">{money(r.ahorro_red)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
